import { takeEvery, put, all, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { listActions, listReducer } from '@nextiva/engagement-ui';

import {
  SURVEY_STATUS_ARCHIVED,
  SURVEY_STATUS_CLOSED,
  SURVEY_STATUS_DRAFT,
  SURVEY_STATUS_OPEN,
  SURVEY_STATUS_PAUSED,
} from 'sharedParts';
import numbering from 'sharedParts/utils/numbering';
import { SURVEY_LIST } from 'constants/ListNames';
import { FAVORITE, ARCHIVE } from 'constants/surveyList/SurveyTabKeys';
import { CRM_SURVEYS_SHOW_CONNECT_SURVEYS } from 'constants/featureFlags';
import * as actions from 'constants/surveyList/SurveyListActions';
import SurveyListUniqueInitialProps from 'config/surveyList/SurveyListUniqueInitialProps';
import * as surveyApi from 'api/surveyList';
import { getCurrentRouterListRootPath } from 'reducers';
import { getFeatureFlagValue } from 'reducers/appReducer';
import openSurveyAnalytics from 'utils/openSurveyAnalytics';
import { loadSurveyList } from 'actions/surveyListActions';
import { setOverwriteCustomStyles } from 'actions/surveyEditorActions';
import { isCurrentSurveyTabFavorite, isCurrentSurveyTabArchive } from 'reducers/itemListReducer';

const { loadItemList, mountItemList, setItemListTab } = listActions;
const {
  getItemListTabName,
  getSortBy,
  getSortOrder,
  getListItemSearchText,
  getItemListPageNumber,
} = listReducer;

function* onMountSurveyList({ payload: listId }) {
  yield put(mountItemList(listId, SurveyListUniqueInitialProps));
}

function* onLoadSurveyList({ payload: { listId, params, isShowTabsList } }) {
  const isFavorite = yield select(isCurrentSurveyTabFavorite, listId);
  const isArchive = yield select(isCurrentSurveyTabArchive, listId);
  const sortOrder = yield select(getSortOrder, listId);
  const sortBy = yield select(getSortBy, listId);
  const search = yield select(getListItemSearchText, listId);
  const pageNumber = yield select(getItemListPageNumber, listId);
  const showConnectSurveys = yield select(getFeatureFlagValue, CRM_SURVEYS_SHOW_CONNECT_SURVEYS);

  yield put(
    loadItemList(listId, surveyApi.getSurveys, {
      pageNumber,
      search,
      sortBy,
      sortOrder,
      onlyFavorites: isFavorite,
      showConnectSurveys,
      statuses: isShowTabsList
        ? [
            SURVEY_STATUS_ARCHIVED,
            SURVEY_STATUS_CLOSED,
            SURVEY_STATUS_DRAFT,
            SURVEY_STATUS_OPEN,
            SURVEY_STATUS_PAUSED,
          ]
        : null,
      ...params,
    }),
  );

  yield put(setOverwriteCustomStyles(false));
}

function* onSurveyTabChange({ payload: { listId, tabName, pageNumber = 0 } }) {
  yield put(setItemListTab(listId, tabName));
  yield put(
    loadSurveyList(listId, {
      pageNumber,
      onlyFavorites: tabName === FAVORITE,
      statuses:
        tabName === ARCHIVE
          ? [SURVEY_STATUS_ARCHIVED]
          : [
              SURVEY_STATUS_ARCHIVED,
              SURVEY_STATUS_CLOSED,
              SURVEY_STATUS_DRAFT,
              SURVEY_STATUS_OPEN,
              SURVEY_STATUS_PAUSED,
            ],
    }),
  );
}

function* onGoBackToSurveyList() {
  const pageNumber = yield select(getItemListPageNumber, SURVEY_LIST);
  const tabName = yield select(getItemListTabName, SURVEY_LIST);
  const rootPath = yield select(getCurrentRouterListRootPath);

  yield put(push(`${rootPath}/${numbering.toHuman(pageNumber)}/${tabName}`));
}

function* onForwardToAnalytics({ payload: id }) {
  openSurveyAnalytics(id);
}

export default function*() {
  yield all([
    takeEvery(actions.MOUNT_SURVEY_LIST, onMountSurveyList),
    takeEvery(actions.LOAD_SURVEY_LIST, onLoadSurveyList),
    takeEvery(actions.SURVEY_TAB_CHANGE, onSurveyTabChange),
    takeEvery(actions.GO_BACK_TO_SURVEY_LIST, onGoBackToSurveyList),
    takeEvery(actions.FORWARD_TO_ANALYTICS, onForwardToAnalytics),
  ]);
}
